export enum JobStatisticReportRange {
	Monthly = 0,
	SixMonth = 1,
	Yearly = 2
}

export enum JobKind {
	Job = 0,
	Internship = 1
}

export enum JobStatus {
	Draft = 0,
	Published = 1,
	Unpublished = 2,
	Expired = 3
}

export type JobFilterType =
	| 'JobTags/Title'
	| 'JobActivities/Title'
	| 'Locations/Title'
	| 'BusinessSectors/Title';

export interface JobFacet {
	facet:
		| 'JobTags/Title'
		| 'JobActivities/Title'
		| 'Locations/Title'
		| 'BusinessSectors/Title';
	items: {
		value: string;
		count: number;
		from: number;
		to: number;
		type: string;
		isChecked: boolean;
	}[];
}
export interface PublishJobPartner {
	jobId: string;
	tenantId: string;
	featuredPhoto: string | null;
	title: string;
	shortDescription: string;
	description: string;
	kind: JobKind;
	jobTags: {
		jobTagId: string;
		title: string;
		description: string;
		categoryTitle: string;
	}[];
	jobActivities: {
		jobActivityId: string | null;
		title: string;
	}[];
	locations: {
		postalCode: string;
		regionCode: string;
		title: string;
	}[];
	communityPartnerId: string[];
	partnerName: string;
	partnerLogo: string | null;
	publishedDate: string;
	jobInsightSummaryReport: {
		date: string;
		jobViewCount: number;
		totalAppliedJob: number;
		distinctUserViewCount: number;
	}[];
	partnerAddress: {
		name: string;
		countryName: string;
		countryIso2: string;
		countryIso3: string;
		state: string;
		city: string;
		postalCode: string;
		street: string;
		houseNumber: string;
	};
	businessSectors: {
		businessSectorId: string;
		title: string;
	}[];
	slugUrl: string;
	jobPublishedVisibilities: {
		communityPartnerId: string;
		topJob: boolean;
		topJobExpiredAt: string | null;
	}[];
}

export interface PublishJob {
	results: [
		{
			score: number;
			highlights: {
				additionalProp1: string[];
				additionalProp2: string[];
				additionalProp3: string[];
			};
			semanticSearch: {
				rerankerScore: number;
				captions: [
					{
						text: string;
						highlights: string;
						additionalProperties: {
							additionalProp1: string;
							additionalProp2: string;
							additionalProp3: string;
						};
					}
				];
			};
			document: PublishJobPartner;
		}
	];
	facets: JobFacet[];
	filter: {
		keyword: string;
		jobTags: string[];
		jobActivities: string[];
		locations: any[];
		userLocation: null;
		providedAddress: null;
	};
	totalCount: number;
	currentPage: number;
	pageSize: number;
	totalPages: number;
	hasNextPage: boolean;
	hasPreviousPage: boolean;
}

export interface PublishJobFilterItem {
	value: string;
	count: number;
	from: number;
	to: number;
	type: string;
	isChecked: boolean;
}

export interface PublishJobItem {
	jobId: string;
	tenantId: string;
	title: string;
	shortDescription: string;
	description: string;
	featuredPhoto: string;
	jobTags: any[];
	jobActivities: any[];
	kind: JobKind;
	primaryLocation: {
		coordinates: {
			altitude: number;
			longitude: number;
			latitude: number;
			count: number;
		};
		type: number;
		boundingBox: {
			west: number;
			south: number;
			east: number;
			north: number;
			minAltitude: number;
			maxAltitude: number;
		};
	};
	partnerName: string;
	partnerLogo: string;
	partnerAddress: {
		name: string;
		countryName: string;
		countryIso2: string;
		countryIso3: string;
		state: string;
		city: string;
		postalCode: string;
		street: string;
		houseNumber: string;
	};
	locations: {
		postalCode: string;
		regionCode: string;
		title: string;
		point: {
			coordinates: {
				altitude: number;
				longitude: number;
				latitude: number;
				count: number;
			};
		};
		type: number;
		boundingBox: {
			west: number;
			south: number;
			east: number;
			north: number;
			minAltitude: number;
			maxAltitude: number;
		};
	}[];
	slugUrl: string;
}

export interface Job {
	auditInfo: AuditInfo;
	code: string;
	id: string;
	businessSectors: {
		businessSectorId: string;
		title: string;
		translateTitles: null;
	}[];
	tenantId?: string;
	jobVisibilities: any;
	jobStatus: number;
	kind: number;
	locations: [
		{
			postalCode: string;
			address: string;
			primary: boolean;
			regionCode: number;
			title: string;
			locationPoint: {
				longitude: number;
				latitude: number;
			};
		}
	];
	partnerName: string;
	jobPeriods?: internshipPeriods[];
	expiredDate: string | undefined | Date;
	startDate: string | undefined | Date;
	partnerType?: number;
	publishedVersion: number | null;
	slugUrl: string;
	title: string;
	publishedBy?: string;
	publishedAt?: string;
	featuredPhoto: { url: string; thumbnail: string };
	featuredVideo: { url: string; thumbnail: string; mimeType: string };
	version: number;
	shortDescription: string;
	description: string;
	benefitsText: string;
	requirementsText: string;
	responsibilitiesText: string;
	showBenefitsText: boolean;
	showRequirementsText: boolean;
	showResponsibilitiesText: boolean;
	showDescription: boolean;
	contactPerson: JobContactPerson | null;
	jobTags: JobTagsOption[] | null;
	jobActivities: string[] | null;
}

export interface internshipPeriods {
	id?: string;
	title: string;
	from: string;
	to: string;
	startTime?: string;
	endTime?: string;
	includeTimeRange: boolean;
}

export interface TranslateJob {
	language: string;
	value: string;
}

export interface AuditInfo {
	createdBy: string;
	createdAt: string;
	updatedBy: string;
	updatedAt: string;
}

export interface JobTagCategories {
	id: string;
	partitionKey: string;
	title: string;
	description: string;
	icon: string;
	order: number;
	translateTitles: TranslateJob[];
	translateDescriptions: TranslateJob[];
	auditInfo: AuditInfo;
	isActive: boolean;
}

export interface ActivityTag {
	id: string;
	partitionKey: string;
	title: string;
	code: string;
	translateTitles: TranslateJob;
	auditInfo: AuditInfo;
	isActive: boolean;
}

export interface JobTags {
	id: string;
	jobTagCategoryId: string;
	partitionKey: string;
	title: string;
	description: string;
	translateTitles: TranslateJob[];
	translateDescriptions: TranslateJob[];
	auditInfo: AuditInfo;
	isActive: boolean;
}

export interface JobFormGeneral {
	code: string;
	title: string;
	externalLink: string;
	kind?: number;
	jobStatus?: number;
	shortDescription: string;
	startDate: string | undefined | Date;
	expiredDate: string | undefined | Date;
	isActive: boolean;
	featuredVideo?: { url: string; thumbnail: string; mimeType: string };
	hasStartDate?: boolean;
	hasExpireDate?: boolean;
}

export interface JobFormFeatured {
	featuredPhoto: { url: string; thumbnail: string };
	featuredVideo: { url: string; thumbnail: string; mimeType: string };
}

export interface JobContactPerson {
	fullName: string;
	emailAddress: string;
	phoneNumber: string;
	avatar: { url: string; thumbnail: string };
	visibleInPublic: boolean;
}

export interface JobTagsOption {
	jobTagId: string;
	order: number;
	title?: string;
}

export interface JobFormInfo {
	jobTags: JobTagsOption[] | null;
	jobActivities: string[] | null;
}

export interface JobVisibilitySetting {
	jobLocations: [
		{
			postalCode: string;
			address: string | any;
			primary: boolean;
			regionCode: number;
			title: string;
			name?: string;
			locationPoint: {
				longitude: number;
				latitude: number;
			};
		}
	];
	jobVisibilities: {
		subscriptionId: string;
		communityPartnerId: string;
	}[];
}

export interface JobFormDescription {
	description: string;
	benefitsText: string;
	requirementsText: string;
	responsibilitiesText: string;
	showDescription: boolean;
	showBenefitsText: boolean;
	showRequirementsText: boolean;
	showResponsibilitiesText: boolean;
}

export interface JobInternshipType {
	title: string;
	from: string;
	to: string;
	includeTimeRange: boolean;
	startTime?: string;
	endTime?: string;
}

export type JobFormInitialData = JobFormGeneral &
	JobFormInfo &
	JobFormFeatured & {
		jobPeriods: JobInternshipType[];
	} & JobFormDescription & {
		contactPerson: JobContactPerson | null;
	} & JobVisibilitySetting & {
		allowPublish: boolean;
	};

export interface JobForm {
	step0: { kind: number };
	step1: JobFormGeneral;
	step2: JobFormFeatured;
	step3: JobFormInfo;
	step4: JobFormDescription;
	step5: JobInternshipType[];
	step6: JobVisibilitySetting;
}

export interface JobFormData {
	id?: string;
	auditInfo?: AuditInfo;
	code: string;
	title: string;
	jobPeriods?: JobInternshipType[];
	kind: number;
	jobStatus?: number;
	shortDescription: string;
	partnerName: string;
	locations?: [
		{
			postalCode: string;
			address: string | any;
			primary: boolean;
			regionCode: number;
			title: string;
			name?: string;
			locationPoint: {
				longitude: number;
				latitude: number;
			};
		}
	];
	jobLocations: [
		{
			postalCode: string;
			address: string | any;
			primary: boolean;
			regionCode: number;
			title: string;
			name?: string;
			locationPoint: {
				longitude: number;
				latitude: number;
			};
		}
	];
	isActive: boolean;
	jobTags: JobTagsOption[];
	featuredPhoto?: {
		url: string;
		thumbnail: string;
	} | null;
	featuredVideo?: {
		url: string;
		thumbnail: string;
		mimeType: string;
	} | null;
	publishedVersion?: number | boolean;
	version?: number;
	startDate: string | undefined | Date;
	expiredDate: string | undefined | Date;
	partnerType?: number;
	jobActivities: any[];
	description: string;
	benefitsText: string;
	requirementsText: string;
	responsibilitiesText: string;
	contactPerson: JobContactPerson | null;
	showBenefitsText: boolean;
	showRequirementsText: boolean;
	showResponsibilitiesText: boolean;
	showDescription: boolean;
	jobVisibilities?: {
		subscriptionId: string;
		communityPartnerId: string;
		topJob: boolean;
		topJobExpiredAt: string;
		topJobPublishedAt: string;
	}[];
}

export interface JobTop {
	subscriptionId: string;
	communityPartnerId: string;
}

export interface JobStatistic {
	id: string;
	draft: number;
	autoPublished: number;
	published: number;
	expired: number;
	unpublished: number;
	companyId: string;
	createdAt: string;
}

export interface TotalJobStatistic {
	id: string;
	totalDraft: number;
	totalAutoPublished: number;
	totalPublished: number;
	totalExpired: number;
	totalUnpublished: number;
	companyId: string;
	createdAt: string;
}

export interface JobUsage {
	kind: JobKind;
	usage: string;
	capacity: number;
	count: number;
	subscriptionId: string;
	topJobCount: number;
	topJobPublishedAt: string;
}

export interface JobGenerateDescriptionByAIFormData {
	jobTitle: string;
	requirement: string;
	languageCode: any;
	maxWord: number;
}

export interface JobGenerateDescriptionByAIResult {
	description: string;
	requirements: string;
	responsibilities: any;
}

export interface JobSelectedFilter {
	keyword: string;
	jobTags: string[];
	jobActivities: string[];
	locations: any[];
	userLocation: null;
	providedAddress: null;
}

export interface JobSearchAnalytics {
	jobId: string;
	jobTitle: string;
	jobStatus: JobStatus;
	totalView: number;
	totalApply: number;
	jobKind: JobKind;
	publishedAt: string;
	visibility: {
		subscriptionId: string;
		communityPartnerId: string;
		topJob: boolean;
		topJobExpiredAt: string | null;
		topJobPublishedAt: string | null;
	} | null;
}
